import React from "react";
import { aboutParagraphs, education, principles, profile } from "../../mock";
import { BadgeCheck, GraduationCap, Award } from "lucide-react";

const SectionLabel = ({ index, title }) => (
  <div className="flex items-center gap-3 mb-10">
    <span className="font-mono text-[11px] tracking-[0.22em] text-[#B8542B]">
      {index}
    </span>
    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#6B5F52]">
      {title}
    </span>
    <span className="flex-1 h-px bg-[#E8E0D3]" />
  </div>
);

const About = () => {
  return (
    <section id="about" className="py-24 md:py-32 px-6 md:px-10">
      <div className="max-w-6xl mx-auto">
        <SectionLabel index="01" title="About" />

        <div className="grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-7">
            <h2 className="font-serif text-3xl md:text-5xl text-[#1F1B16] leading-[1.1] tracking-tight">
              I design quality the way good editors
              <span className="italic text-[#8a5a3d]"> shape sentences</span>
              {" "}— quietly, and everywhere it matters.
            </h2>

            <div className="mt-10 space-y-5">
              {aboutParagraphs.map((p, i) => (
                <p
                  key={i}
                  className="text-[#3a342d] text-[16.5px] md:text-lg leading-[1.75] max-w-2xl"
                >
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-[#E8E0D3]">
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#8a7d6e] mb-5">
                <Award size={14} className="text-[#B8542B]" />
                Certifications
              </div>
              <div className="flex flex-wrap gap-2">
                {profile.certifications.map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#E8E0D3] bg-[#F5F0E8]/60 text-[#3a342d] text-xs"
                  >
                    <BadgeCheck size={14} className="text-[#B8542B]" />
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="md:col-span-5 md:pl-10 md:border-l md:border-[#E8E0D3]">
            {/* Portrait photo */}
            <div className="mb-10">
              <div
                className="relative w-full overflow-hidden rounded-md ring-1 ring-[#E8E0D3]"
                style={{
                  aspectRatio: "4 / 5",
                  backgroundImage: `url(${profile.photo})`,
                  backgroundSize: "cover",
                  backgroundPosition: "30% center",
                  boxShadow:
                    "0 30px 60px -25px rgba(31,27,22,0.30), 0 8px 24px -8px rgba(138,90,61,0.20)",
                }}
                aria-label={`Portrait of ${profile.name}`}
              >
                {/* Inner ring highlight */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 rounded-md"
                  style={{
                    boxShadow:
                      "inset 0 0 0 1px rgba(255,255,255,0.55), inset 0 -1px 0 rgba(31,27,22,0.05)",
                  }}
                />
              </div>
            </div>

            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#8a7d6e] mb-6">
              Operating principles
            </div>
            <ul className="space-y-7">
              {principles.map((p, i) => (
                <li key={p.title} className="group">
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-[10px] text-[#B8542B]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-serif text-xl text-[#1F1B16]">
                      {p.title}
                    </h3>
                  </div>
                  <p className="mt-2 ml-8 text-[#6B5F52] text-[15px] leading-relaxed">
                    {p.body}
                  </p>
                </li>
              ))}
            </ul>

            {/* Education block */}
            <div className="mt-12 pt-8 border-t border-[#E8E0D3]">
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#8a7d6e] mb-5">
                <GraduationCap size={14} className="text-[#B8542B]" />
                Education
              </div>
              <ul className="space-y-5">
                {education.map((ed) => (
                  <li key={ed.school}>
                    <div className="font-serif text-[18px] text-[#1F1B16] leading-snug">
                      {ed.degree}
                    </div>
                    <div className="mt-1 text-[14px] text-[#6B5F52] italic">
                      {ed.school}
                    </div>
                    <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#8a7d6e]">
                      {ed.period}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
